import { CircleCheck, Trophy } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { CcLandingMock } from "./CcLandingMock";
import type { LandingSubpageCopy } from "./landing-page-copy";

type ViewProps = {
  copy: LandingSubpageCopy["anatomy"];
  mock: LandingSubpageCopy["mock"];
};

function PhoneFrame({ children, loading }: { children: ReactNode; loading?: string }) {
  return (
    <div className="cc-lp-phone">
      <span className="cc-lp-phone__island" />
      {loading ? (
        <span className="cc-lp-phone__progress">
          <i />
        </span>
      ) : null}
      <div className="cc-lp-phone__screen">{children}</div>
    </div>
  );
}

/**
 * "Fast loading on mobile": the page on a phone with a load bar that fills
 * at once, Core Web Vitals rated good, and the same ad click on a light
 * versus a heavy page.
 */
export function CcLandingSpeedView({ copy, mock }: ViewProps) {
  const view = copy.speedView;
  return (
    <div className="cc-lp-speed">
      <div className="cc-lp-speed__phone">
        <PhoneFrame loading={view.loaded}>
          <CcLandingMock copy={mock} state="after" device="phone" />
        </PhoneFrame>
        <span className="cc-lp-speed__loaded">
          <CircleCheck size={12} strokeWidth={2.6} />
          {view.loaded}
        </span>
      </div>
      <div className="cc-lp-speed__side">
        <div className="cc-lp-speed__card">
          <span className="cc-lp-speed__head">
            <strong>{view.vitalsTitle}</strong>
            <b>{view.status}</b>
          </span>
          {view.rows.map((row, index) => (
            <span key={row.label} className="cc-lp-speed__row" style={{ "--cc-lp-i": index } as CSSProperties}>
              <i />
              <strong>{row.label}</strong>
              <span>{row.text}</span>
              <em>{view.rating}</em>
            </span>
          ))}
        </div>
        <div className="cc-lp-speed__card">
          <strong className="cc-lp-speed__compare-title">{view.compareTitle}</strong>
          <span className="cc-lp-speed__race cc-lp-speed__race--fast">
            <span>
              <b>{view.fast}</b>
              <small>{view.fastNote}</small>
            </span>
            <span className="cc-lp-speed__track">
              <i />
            </span>
          </span>
          <span className="cc-lp-speed__race cc-lp-speed__race--slow">
            <span>
              <b>{view.slow}</b>
              <small>{view.slowNote}</small>
            </span>
            <span className="cc-lp-speed__track">
              <i />
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * "A/B tests instead of guesswork": two versions on two phones, each shown
 * to half the visitors, with enquiries side by side and the winner marked.
 */
export function CcLandingAbView({ copy, mock }: ViewProps) {
  const view = copy.abView;
  const variants = [
    { ...view.a, bar: "44%", winner: false },
    { ...view.b, bar: "82%", winner: true },
  ];
  return (
    <div className="cc-lp-ab">
      <span className="cc-lp-ab__title">
        <i />
        {view.title}
      </span>
      <div className="cc-lp-ab__grid">
        {variants.map((variant) => (
          <div key={variant.label} className="cc-lp-ab__variant" data-winner={variant.winner ? "true" : undefined}>
            <span className="cc-lp-ab__label">
              <strong>{variant.label}</strong>
              <small>{view.split}</small>
            </span>
            <PhoneFrame>
              <CcLandingMock copy={mock} state="after" device="phone" variant={variant} />
            </PhoneFrame>
            <span className="cc-lp-ab__result">
              <span className="cc-lp-ab__metric">
                {view.metric}
                {variant.winner ? (
                  <b>
                    <Trophy size={10} strokeWidth={2.4} />
                    {view.winner}
                  </b>
                ) : null}
              </span>
              <span className="cc-lp-ab__bar" style={{ "--cc-lp-bar": variant.bar } as CSSProperties}>
                <i />
              </span>
            </span>
          </div>
        ))}
      </div>
      <small className="cc-lp-ab__note">{view.note}</small>
    </div>
  );
}
